import { RepeatWrapping, Texture, TextureLoader } from "three";
import { create } from "zustand";
import { PresetMaterial } from "../3d/material/PresetMaterial";
import { isNotEmpty } from "@/utils/typeGuards";
import { round } from "@/3d/math";
import { MaterialResources } from "./MaterialResources";
import { Furniture, FurnitureWithModel } from "./Furniture";
import {
    FurnitureDto,
    initFurniture,
    loadFurnitureDataFromApi,
    loadFurnitureDataFromStaticJson,
} from "@/api/furniture";
import { useEffect, useState } from "react";
import { loadModelWithConfig } from "@/api/model";

type ResourcesState = {
    materials: Record<string, MaterialResources>;
    furniture: Record<string, Furniture>;
    loadingProgress: number;
    loadingMessage: string;
    isLoaded: boolean;

    load: () => Promise<void>;
    loadFurnitureModel: (id: string) => Promise<FurnitureWithModel | undefined>;
};

const rootPath = "/assets/";

export const useResourcesState = create<ResourcesState>((set, get) => ({
    materials: {},
    furniture: {},
    loadingProgress: 0,
    loadingMessage: "",
    isLoaded: false,

    load: async () => {
        if (Object.keys(get().materials).length > 0) {
            return;
        }

        const configs = (await fetch(rootPath + "/materials/materials.json").then((res) =>
            res.json()
        )) as PresetMaterial[];

        set({ loadingProgress: 0, loadingMessage: "Загрузка материалов..." });

        const loadedMaterials: Record<string, MaterialResources> = {};
        let counter = 0;
        const total = configs.flatMap((c) => Object.values(c.maps).filter((m) => !!m)).length;

        for (const config of configs) {
            const resources = await loadMaterialByConfig(config, (cnt) => {
                counter += cnt;
                const progress = round(((counter + 1) / total) * 100, 1);
                set({
                    loadingProgress: progress,
                    loadingMessage: `Загрузка материалов: ${progress}%`,
                });
            });
            loadedMaterials[config.name] = resources;
        }

        let apiFurnitureConfig: ReadonlyArray<FurnitureDto> = [];
        try {
            apiFurnitureConfig = await loadFurnitureDataFromApi();
        } catch (e) {
            console.error("Failed to load furniture data from api", e);
        }

        const staticFurnitureConfig = await loadFurnitureDataFromStaticJson("furniture.json");
        const furnitureGeneratedConfig = await loadFurnitureDataFromStaticJson(
            "furniture.generated.json"
        );
        const furniture = await initFurniture(
            [...staticFurnitureConfig, ...furnitureGeneratedConfig, ...apiFurnitureConfig],
            (e) => {
                set({
                    loadingProgress: e,
                    loadingMessage: `Загрузка мебели: ${round(e * 100, 1)}%`,
                });
            }
        );

        set({ furniture, materials: loadedMaterials, isLoaded: true });
    },
    loadFurnitureModel: async (id: string): Promise<FurnitureWithModel | undefined> => {
        const furniture = get().furniture;
        const variant = furniture[id];
        if (!variant) {
            return undefined;
        }

        let model = variant.model;
        if (model) {
            return { ...variant, model };
        }

        model = await loadModelWithConfig({
            modelPath: variant.resources.modelPath,
            configPath: variant.resources.configPath,
        });

        if (!model) {
            return undefined;
        }

        set({ furniture: { ...furniture, [id]: { ...variant, model } } });
        return { ...variant, model };
    },
}));

const loadMaterialByConfig = async (
    config: PresetMaterial,
    onProgress: (e: number) => void
): Promise<MaterialResources> => {
    const loader = new TextureLoader();

    const handleLoadFinish = (value: Texture) => {
        onProgress(1);

        value.wrapS = RepeatWrapping;
        value.wrapT = RepeatWrapping;

        return value;
    };

    const color = await loader.loadAsync(config.path + config.maps.color).then(handleLoadFinish);

    const displacement = isNotEmpty(config.maps.displacement)
        ? await loader.loadAsync(config.path + config.maps.displacement).then(handleLoadFinish)
        : undefined;

    const normal = isNotEmpty(config.maps.normal)
        ? await loader.loadAsync(config.path + config.maps.normal).then(handleLoadFinish)
        : undefined;

    const roughness = isNotEmpty(config.maps.roughness)
        ? await loader.loadAsync(config.path + config.maps.roughness).then(handleLoadFinish)
        : undefined;

    const metalness =
        config.maps.metalness && isNotEmpty(config.maps.metalness)
            ? await loader.loadAsync(config.path + config.maps.metalness).then(handleLoadFinish)
            : undefined;

    return {
        config,
        textures: { color, displacement, normal, roughness, metalness, isCommon: true },
    };
};

const getFurnitureVariant = (
    furniture: Record<string, Furniture>,
    data: { id: string } | { name: string }
): Furniture | undefined => {
    const variant = Object.values(furniture).find((f) => {
        if ("id" in data) {
            return f.id === data.id;
        }

        return f.data.name === data.name;
    });

    return variant;
};

const getFurnitureWithModel = (
    furniture: Record<string, Furniture>,
    data: { id: string } | { name: string }
): FurnitureWithModel | undefined => {
    const variant = getFurnitureVariant(furniture, data);
    return variant?.model ? { ...variant, model: variant.model } : undefined;
};

export const useFurnitureWithModel = (
    data: { id: string } | { name: string }
): FurnitureWithModel | undefined => {
    const furniture = useResourcesState((state) => state.furniture);

    const [furnitureWithModel, setFurnitureWithModel] = useState<FurnitureWithModel | undefined>(
        getFurnitureWithModel(furniture, data)
    );
    const loadFurnitureModel = useResourcesState((state) => state.loadFurnitureModel);

    useEffect(() => {
        const variant = getFurnitureVariant(furniture, data);

        if (!variant) {
            setFurnitureWithModel(undefined);
            return;
        }

        const model = variant.model;
        if (model) {
            setFurnitureWithModel({ ...variant, model });
            return;
        }

        loadFurnitureModel(variant.id).then((furnitureWithModel) => {
            if (!furnitureWithModel) {
                return;
            }

            setFurnitureWithModel(furnitureWithModel);
        });
    }, []);

    return furnitureWithModel;
};
